import { useApiQuery } from '@common/hooks/query/useApiQuery';
import { SkipOrArgs } from '@common/types/queryState';
import { hasValues, isEmpty, isObject } from '@common/utils';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { FALLBACK_LOCALES, SupportedLocaleCode } from '@constants/locales';
import { useGetCollectionMetadataQuery } from '../api';
import {
    CollectionMetadata,
    DEFAULT_COLLECTION_METADATA_RESPONSE,
    DEFAULT_SYNOPSIS,
    GetCollectionMetadataArgs,
    MultilingualCollectionMetadata,
    Synopses,
} from '../metadataTypes';

import { useMemo } from 'react';
import { useLanguage } from '@features/language';

export type UseCollectionMetadataArgs = GetCollectionMetadataArgs;

const getLocalizedField = <T>(
    field: Record<SupportedLocaleCode, T> | undefined,
    locale: SupportedLocaleCode,
    fallback: T,
): T => {
    if (!field) return fallback;

    const searchOrder = [locale, FALLBACK_LOCALES[locale]].filter(Boolean);

    const validLocale = searchOrder.find((currentLocale) => {
        const value = field[currentLocale];

        if (isEmpty(value)) return false;
        if (isObject(value) && !hasValues(value as object)) return false;

        return true;
    });

    return validLocale ? field[validLocale] : fallback;
};

const flattenMetadata = (
    fatMetadata: MultilingualCollectionMetadata,
    locale: SupportedLocaleCode,
): CollectionMetadata => {
    return {
        ...fatMetadata,
        title: getLocalizedField<string>(fatMetadata.title, locale, ''),
        genres: getLocalizedField<string[]>(fatMetadata.genres, locale, []),
        synopses: getLocalizedField<Synopses>(
            fatMetadata.synopses,
            locale,
            DEFAULT_SYNOPSIS,
        ),
    };
};

export const useCollectionMetadata = (
    args: SkipOrArgs<UseCollectionMetadataArgs>,
) => {
    const {
        currentLanguage: { code: locale },
    } = useLanguage();
    const { data, ...queryResult } = useApiQuery(
        useGetCollectionMetadataQuery,
        args,
    );

    const metadata = mergeWithDefault(
        data,
        DEFAULT_COLLECTION_METADATA_RESPONSE,
    );

    const flatMergedData: CollectionMetadata = useMemo(() => {
        return flattenMetadata(metadata.merged, locale);
    }, [metadata.merged, locale]);

    return {
        data: flatMergedData,
        sources: metadata.sources,
        ...queryResult,
    };
};
